import api from "@/lib/axiosConfig.ts";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";

export const SHEET_REVISION_ENTITY_TYPES = [
  "artwork",
  "multipleprint",
  "exhibition",
  "bibliography",
  "auction",
  "author",
  "collector",
  "audiovisual",
  "document",
  "writing",
  "photography",
] as const;

export type SheetRevisionEntityType = (typeof SHEET_REVISION_ENTITY_TYPES)[number];

export function isSheetRevisionEntityType(
  t: string,
): t is SheetRevisionEntityType {
  return (SHEET_REVISION_ENTITY_TYPES as readonly string[]).includes(t);
}

export type SheetRevisionActor = {
  id: string;
  name: string;
  lastName: string;
  email: string;
  login: string;
};

export type SheetRevisionListItem = {
  id: string;
  instanceId: string;
  entityType: SheetRevisionEntityType;
  entityId: string;
  action: "create" | "update" | "restore";
  actorUserId: string | null;
  payload: unknown;
  createdAt: string;
  actor: SheetRevisionActor | null;
};

export type SheetRevisionListResponse = {
  items: SheetRevisionListItem[];
  total: number;
};

const basePath = "back/api/sheet-revisions";

export async function fetchSheetRevisions(
  entityType: SheetRevisionEntityType,
  entityId: string,
  take = 50,
  skip = 0,
): Promise<SheetRevisionListResponse> {
  const { data } = await api.get<SheetRevisionListResponse>(basePath, {
    params: { entityType, entityId, take, skip },
  });
  return data;
}

export async function fetchSheetRevision(
  revisionId: string,
): Promise<SheetRevisionListItem> {
  const { data } = await api.get<SheetRevisionListItem>(
    `${basePath}/${revisionId}`,
  );
  return data;
}

export async function restoreSheetRevision(revisionId: string): Promise<unknown> {
  const { data } = await api.post(`${basePath}/restore`, { revisionId });
  return data;
}

export function sheetRevisionsQueryKey(
  entityType: SheetRevisionEntityType,
  entityId: string,
) {
  return ["sheet-revisions", entityType, entityId] as const;
}

export function useSheetRevisions(
  entityType: SheetRevisionEntityType | null,
  entityId: string | undefined,
  options?: Partial<UseQueryOptions<SheetRevisionListResponse>>,
) {
  return useQuery({
    queryKey:
      entityType && entityId
        ? sheetRevisionsQueryKey(entityType, entityId)
        : ["sheet-revisions", "disabled"],
    queryFn: () =>
      fetchSheetRevisions(entityType!, entityId!, 50, 0),
    enabled: Boolean(entityType && entityId),
    ...options,
  });
}

export function useSheetRevisionDetail(
  revisionId: string | null,
  options?: Partial<UseQueryOptions<SheetRevisionListItem>>,
) {
  return useQuery({
    queryKey: ["sheet-revision", revisionId],
    queryFn: () => fetchSheetRevision(revisionId!),
    enabled: Boolean(revisionId),
    ...options,
  });
}

/** React Query key prefix for the single-entity fetch hook per resource type. */
const ENTITY_DETAIL_QUERY_PREFIX: Record<SheetRevisionEntityType, string> = {
  artwork: "artworks",
  multipleprint: "multipleprint",
  exhibition: "exhibition",
  bibliography: "bibliography",
  auction: "auction",
  author: "author",
  collector: "collector",
  audiovisual: "audiovisual",
  document: "document",
  writing: "writing",
  photography: "photography",
};

export function useRestoreSheetRevision(
  entityType: SheetRevisionEntityType,
  entityId: string,
  onSuccess?: () => void,
) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (revisionId: string) => restoreSheetRevision(revisionId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: sheetRevisionsQueryKey(entityType, entityId),
      });
      void qc.invalidateQueries({ queryKey: ["sheet-revision"] });
      void qc.invalidateQueries({
        queryKey: [ENTITY_DETAIL_QUERY_PREFIX[entityType], entityId],
      });
      onSuccess?.();
    },
  });
}
